import api from 'api';
import Button from 'components/Button';
import FormGroup from 'components/FormGroup';
import Modal from 'components/Modal';
import TextField from 'components/TextField';
import Typography from 'components/Typography';
import {get} from 'lodash';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import toast from 'react-hot-toast';
import {useMutation, useQueryClient} from 'react-query';
import styles from './RenameRouteModal.module.scss';

type RenameRouteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  routeId: number;
  routeName: string;
};

const renameRoute = async ({
  routeId,
  name,
}: {
  routeId: number;
  name: string;
}) => {
  const {data} = await api.patch(`/routes/${routeId}/`, {name});
  return data;
};

function RenameRouteModal({
  isOpen,
  onClose,
  routeId,
  routeName,
}: RenameRouteModalProps) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm<{name: string}>({
    defaultValues: {
      name: routeName,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({name: routeName});
    }
  }, [isOpen, routeName, reset]);

  const {mutate: renameRouteMutation, isLoading} = useMutation({
    mutationKey: 'rename_route',
    mutationFn: renameRoute,
    onSuccess: () => {
      toast.success('Route renamed successfully');
      queryClient.invalidateQueries('routes');
      onClose();
    },
    onError: (e) => {
      const message = get(e, 'response.data.detail', 'Failed to rename route');
      toast.error(message);
    },
  });

  const handleRenameRoute = (data: {name: string}) => {
    renameRouteMutation({routeId, name: data.name});
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onRequestClose={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className={styles.header}>
        <Typography variant="h2">Edit Route Name</Typography>
      </div>
      <form onSubmit={handleSubmit(handleRenameRoute)}>
        <FormGroup columns={1}>
          <TextField
            label="Route name"
            placeholder="Enter route name"
            error={errors.name?.message}
            {...register('name', {required: 'Route name is required'})}
          />
          <div style={{display: 'flex', justifyContent: 'flex-end'}}>
            <Button type="submit" loading={isLoading}>
              Rename
            </Button>
          </div>
        </FormGroup>
      </form>
    </Modal>
  );
}

export default RenameRouteModal;
